// Admin query layer. Pairs with `lib/mutations/admin.ts`.
// No 'use client' directive: hooks are React hooks called from client
// components; server pages import the query keys to prefetch.

import {
  infiniteQueryOptions,
  queryOptions,
  useQuery,
  useInfiniteQuery,
  keepPreviousData,
} from '@tanstack/react-query';
import type { ResourceKind } from '@leanin/shared/resource-kinds';
import type { operations } from '@leanin/api-client';
import { getApiClient } from '../api-client';
import { throwApiError } from '../api-error';

// ---- Re-exported parameter types (mirror views helpers) ----

export type MemberAdminStatus =
  | 'active'
  | 'pending'
  | 'restricted'
  | 'deactivated'
  | 'banned';

export type AdminPlatformRoleFilter = 'admin' | 'moderator' | 'none';
export type AdminCommunityRoleFilter = 'circle_leader' | 'network_leader';
export interface ListAdminMembersInput {
  readonly q?: string;
  readonly status?: MemberAdminStatus;
  readonly platformRole?: AdminPlatformRoleFilter;
  readonly communityRole?: AdminCommunityRoleFilter;
  readonly cursor?: string;
  readonly pageSize?: number;
}

export interface InvitationsPageParams {
  readonly status?: 'pending' | 'accepted' | 'revoked' | 'expired';
  readonly q?: string;
  readonly cursor?: string;
  readonly pageSize?: number;
}

export interface GroupInvitesPageParams {
  readonly status?: 'pending' | 'accepted' | 'revoked' | 'expired';
  readonly groupId?: string;
  readonly cursor?: string;
  readonly pageSize?: number;
}

export interface GroupInvitesSummaryParams {
  readonly status?: 'pending' | 'accepted' | 'revoked' | 'expired';
  readonly cursor?: string;
  readonly pageSize?: number;
}

export interface AuditPageParams {
  readonly action?: string;
  readonly actor?: string;
  readonly outcome?: 'success' | 'failure';
  readonly from?: string;
  readonly to?: string;
  readonly cursor?: string;
  readonly pageSize?: number;
}

export interface ConsentPageParams {
  readonly userId?: string;
  readonly purpose?:
    | 'marketing'
    | 'analytics'
    | 'email_digest'
    | 'ai_processing'
    | 'directory_visible'
    | 'ranking_signal'
    | 'cookie_strict'
    | 'cookie_analytics'
    | 'cookie_marketing';
  readonly granted?: boolean;
  readonly from?: string;
  readonly to?: string;
  readonly cursor?: string;
  readonly pageSize?: number;
}

export interface DsrPageParams {
  readonly status?:
    | 'received'
    | 'approved'
    | 'running'
    | 'completed'
    | 'rejected'
    | 'overdue'
    | 'cancelled';
  readonly kind?: string;
  readonly cursor?: string;
  readonly pageSize?: number;
}

export interface SensitiveReadPageParams {
  readonly actor?: string;
  readonly resourceKind?:
    | 'member_profile'
    | 'message_thread'
    | 'dsr_export_bundle'
    | 'audit_log_query'
    | 'consent_history'
    | 'bulk_export';
  readonly resourceId?: string;
  readonly from?: string;
  readonly to?: string;
  readonly cursor?: string;
  readonly pageSize?: number;
}

export interface BroadcastsPageParams {
  readonly status?: 'draft' | 'scheduled' | 'sent';
  readonly cursor?: string;
  readonly pageSize?: number;
  readonly q?: string;
}

// Query-parameter shape of GET /api/v1/admin/moderation/reports, derived
// from the generated OpenAPI types so a server-side change (a new target
// kind, a new filter) surfaces here as a compile error instead of drifting
// silently — the hand-maintained union this replaces was missing 'circle'.
export type AdminReportQueueInput = NonNullable<
  operations['listAdminModerationReports']['parameters']['query']
>;

export type AdminResourceStatus = 'all' | 'draft' | 'published' | 'featured';
export type AdminResourceContentLayer =
  | 'community'
  | 'network'
  | 'public';

export type AdminResourceScope =
  | 'leadership_library'
  | 'circle_leader_tips'
  | 'network_leader_resources';

export interface ListAdminResourcesInput {
  readonly status?: AdminResourceStatus;
  readonly contentLayer?: AdminResourceContentLayer;
  readonly kind?: ResourceKind;
  readonly scope?: AdminResourceScope;
  readonly q?: string;
  readonly cursor?: string;
  readonly pageSize?: number;
}

export type InterviewSegment =
  | 'circle_leader'
  | 'circle_member'
  | 'network_leader'
  | 'network_member'
  | 'individual'
  | 'any';

export interface InterviewCandidatesInput {
  readonly segment?: InterviewSegment;
  readonly activeDays?: number;
  readonly limit?: number;
  readonly includeContacted?: boolean;
}

export type AudienceSpec =
  | { readonly kind: 'all' }
  | { readonly kind: 'circles'; readonly circleIds: readonly string[] }
  | { readonly kind: 'networks'; readonly networkIds: readonly string[] }
  | { readonly kind: 'members'; readonly userIds: readonly string[] }
  | { readonly kind: 'role'; readonly role: 'leader' | 'admin' }
  | { readonly kind: 'recent_active'; readonly days: number };

export interface PickerInput {
  readonly q?: string;
  readonly cursor?: string;
  readonly pageSize?: number;
}

// ---- Query keys ----

export const adminKeys = {
  all: ['admin'] as const,

  // Inner-node prefix: every member-scoped cache (list/detail/counts/search).
  members: () => [...adminKeys.all, 'members'] as const,

  // membersList — flat (`useAdminMembersPage`) + infinite
  // (`useInfiniteAdminMembers`) share this leaf's key; cursor via pageParam.
  membersList: (params: ListAdminMembersInput) =>
    infiniteQueryOptions({
      queryKey: [...adminKeys.members(), 'list', params] as const,
      queryFn: ({ pageParam }) =>
        fetchAdminMembersPage({
          ...params,
          cursor: (pageParam as string | undefined) ?? params.cursor,
        }),
      initialPageParam: undefined as string | undefined,
      getNextPageParam: (lastPage) =>
        (lastPage as { nextCursor?: string | null }).nextCursor ?? undefined,
    }),

  memberDetail: (id: string) =>
    queryOptions({
      queryKey: [...adminKeys.members(), 'detail', id] as const,
      queryFn: () => fetchAdminMemberDetail(id),
      enabled: id.length > 0,
    }),

  memberStatusCounts: () =>
    queryOptions({
      queryKey: [...adminKeys.members(), 'status-counts'] as const,
      queryFn: fetchAdminMemberStatusCounts,
    }),

  memberSearch: (params: PickerInput) =>
    queryOptions({
      queryKey: [...adminKeys.members(), 'search', params] as const,
      queryFn: () => searchAdminMembersForPicker(params),
    }),

  // Inner-node prefix: every user-research cache.
  userResearch: () => [...adminKeys.all, 'user-research'] as const,

  interviewCandidates: (params: InterviewCandidatesInput) =>
    queryOptions({
      queryKey: [...adminKeys.userResearch(), 'candidates', params] as const,
      queryFn: () => fetchAdminInterviewCandidates(params),
    }),

  invitations: (params: InvitationsPageParams) =>
    queryOptions({
      queryKey: [...adminKeys.all, 'invitations', params] as const,
      queryFn: () => fetchAdminInvitations(params),
    }),

  groupInvites: (params: GroupInvitesPageParams) =>
    queryOptions({
      queryKey: [...adminKeys.all, 'group-invites', params] as const,
      queryFn: () => fetchAdminGroupInvites(params),
    }),

  groupInvitesSummary: (params: GroupInvitesSummaryParams) =>
    queryOptions({
      queryKey: [...adminKeys.all, 'group-invites-summary', params] as const,
      queryFn: () => fetchAdminGroupInvitesSummary(params),
    }),

  referralLinks: () =>
    queryOptions({
      queryKey: [...adminKeys.all, 'referral-links'] as const,
      queryFn: fetchReferralLinks,
    }),

  audit: (params: AuditPageParams) =>
    queryOptions({
      queryKey: [...adminKeys.all, 'audit', params] as const,
      queryFn: () => fetchAdminAudit(params),
    }),

  consent: (params: ConsentPageParams) =>
    queryOptions({
      queryKey: [...adminKeys.all, 'consent', params] as const,
      queryFn: () => fetchAdminConsent(params),
    }),

  dsr: (params: DsrPageParams) =>
    queryOptions({
      queryKey: [...adminKeys.all, 'dsr', 'list', params] as const,
      queryFn: () => fetchAdminDsr(params),
    }),

  dsrDetail: (id: string) =>
    queryOptions({
      queryKey: [...adminKeys.all, 'dsr', 'detail', id] as const,
      queryFn: () => fetchAdminDsrDetail(id),
      enabled: id.length > 0,
    }),

  sensitiveRead: (params: SensitiveReadPageParams) =>
    queryOptions({
      queryKey: [...adminKeys.all, 'sensitive-read', params] as const,
      queryFn: () => fetchAdminSensitiveRead(params),
    }),

  // Inner-node prefix: every broadcast-scoped admin cache.
  broadcasts: () => [...adminKeys.all, 'broadcasts'] as const,

  broadcastsList: (params: BroadcastsPageParams) =>
    queryOptions({
      queryKey: [...adminKeys.broadcasts(), 'list', params] as const,
      queryFn: () => fetchAdminBroadcasts(params),
    }),

  broadcastStatusCounts: () =>
    queryOptions({
      queryKey: [...adminKeys.broadcasts(), 'status-counts'] as const,
      queryFn: fetchAdminBroadcastStatusCounts,
    }),

  broadcastAudience: (spec: AudienceSpec) =>
    queryOptions({
      queryKey: [...adminKeys.broadcasts(), 'audience', spec] as const,
      queryFn: () => fetchAdminBroadcastAudience(spec),
    }),

  // Inner-node prefix: every moderation-scoped admin cache.
  moderation: () => [...adminKeys.all, 'moderation'] as const,

  moderationQueue: (params: AdminReportQueueInput) =>
    queryOptions({
      queryKey: [...adminKeys.moderation(), 'queue', params] as const,
      queryFn: () => fetchAdminModerationQueue(params),
    }),

  moderationReport: (id: string) =>
    queryOptions({
      queryKey: [...adminKeys.moderation(), 'report', id] as const,
      queryFn: () => fetchAdminModerationReport(id),
      enabled: id.length > 0,
    }),

  // The reported DM with up to 4 messages either side — powers the report
  // detail's "View message" preview. A moderator can't open the conversation
  // itself (viewer-scoped route, they're not a member), so this is the only
  // window into the exchange.
  moderationReportMessageContext: (id: string) =>
    queryOptions({
      queryKey: [
        ...adminKeys.moderation(),
        'report',
        id,
        'message-context',
      ] as const,
      queryFn: () => fetchAdminModerationReportMessageContext(id),
      enabled: id.length > 0,
    }),

  // The reported post (or the reported comment's parent post) with a bounded
  // comment window — powers the report detail's "View post" preview. The
  // public /posts/:id route is membership-gated with no admin bypass, so
  // this moderation-scoped window is how a non-member moderator sees the
  // content.
  moderationReportPostContext: (id: string) =>
    queryOptions({
      queryKey: [
        ...adminKeys.moderation(),
        'report',
        id,
        'post-context',
      ] as const,
      queryFn: () => fetchAdminModerationReportPostContext(id),
      enabled: id.length > 0,
    }),

  moderationStats: () =>
    queryOptions({
      queryKey: [...adminKeys.moderation(), 'stats'] as const,
      queryFn: fetchAdminModerationStats,
    }),

  moderationStatusCounts: () =>
    queryOptions({
      queryKey: [...adminKeys.moderation(), 'status-counts'] as const,
      queryFn: fetchAdminModerationStatusCounts,
    }),

  // Inner-node prefix: every admin-resource cache.
  resources: () => [...adminKeys.all, 'resources'] as const,

  resourcesList: (params: ListAdminResourcesInput) =>
    queryOptions({
      queryKey: [...adminKeys.resources(), 'list', params] as const,
      queryFn: () => fetchAdminResourcesPage(params),
    }),

  resourceDetail: (id: string) =>
    queryOptions({
      queryKey: [...adminKeys.resources(), 'detail', id] as const,
      queryFn: () => fetchAdminResourceDetail(id),
      enabled: id.length > 0,
    }),

  // The editor must always initialize from the latest server state, never a
  // cached value. Its own always-fresh key (distinct from `resourceDetail`,
  // staleTime/gcTime 0) means a save → reopen cycle re-seeds the form from the
  // server for every field — title, body, cover, attachments — with no
  // per-mutation cache bookkeeping. A cached editor query is what made an
  // admin's saved edit appear not to persist: the editor remounts inside the
  // global 30s staleTime window and re-reads the pre-save value.
  resourceEditor: (id: string) =>
    queryOptions({
      queryKey: [...adminKeys.resources(), 'editor', id] as const,
      queryFn: () => fetchAdminResourceDetail(id),
      enabled: id.length > 0,
      staleTime: 0,
      gcTime: 0,
    }),

  circlesPicker: (params: PickerInput = {}) =>
    queryOptions({
      queryKey: [...adminKeys.all, 'circles-picker', params] as const,
      queryFn: () => fetchAdminCirclesForPicker(params),
    }),

  networksPicker: (params: PickerInput = {}) =>
    queryOptions({
      queryKey: [...adminKeys.all, 'networks-picker', params] as const,
      queryFn: () => fetchAdminNetworksForPicker(params),
    }),

  // Infinite picker leaves — share the flat picker's key prefix; cursor via
  // pageParam. The flat factories above remain for single-page callers.
  circlesPickerInfinite: (params: Omit<PickerInput, 'cursor'> = {}) =>
    infiniteQueryOptions({
      queryKey: [...adminKeys.all, 'circles-picker', params] as const,
      queryFn: ({ pageParam }) =>
        fetchAdminCirclesForPicker({
          ...params,
          cursor: pageParam as string | undefined,
        }),
      initialPageParam: undefined as string | undefined,
      getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    }),

  networksPickerInfinite: (params: Omit<PickerInput, 'cursor'> = {}) =>
    infiniteQueryOptions({
      queryKey: [...adminKeys.all, 'networks-picker', params] as const,
      queryFn: ({ pageParam }) =>
        fetchAdminNetworksForPicker({
          ...params,
          cursor: pageParam as string | undefined,
        }),
      initialPageParam: undefined as string | undefined,
      getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    }),
};

// ---- Client fetchers ----

export async function fetchAdminMembersPage(params: ListAdminMembersInput) {
  const client = getApiClient();
  const { data, error } = await client.GET('/api/v1/admin/members', {
    params: { query: params },
  });
  if (data === undefined) throwApiError(error);
  return data;
}

export async function fetchAdminMemberDetail(userId: string) {
  const client = getApiClient();
  const { data, error, response } = await client.GET('/api/v1/admin/members/{id}', {
    params: { path: { id: userId } },
  });
  if (data === undefined) throwApiError(error, response);
  return data.data;
}

export async function fetchAdminMemberStatusCounts() {
  const client = getApiClient();
  const { data, error } = await client.GET(
    '/api/v1/admin/members/status-counts',
    {},
  );
  if (data === undefined) throwApiError(error);
  return data;
}

export async function fetchAdminInvitations(params: InvitationsPageParams) {
  const client = getApiClient();
  const { data, error } = await client.GET('/api/v1/admin/invitations', {
    params: { query: params },
  });
  if (data === undefined) throwApiError(error);
  return data;
}

export async function fetchReferralLinks() {
  const client = getApiClient();
  const { data, error } = await client.GET('/api/v1/admin/referral-links');
  if (data === undefined) throwApiError(error);
  return data;
}

export async function fetchAdminGroupInvites(params: GroupInvitesPageParams) {
  const client = getApiClient();
  const { data, error } = await client.GET('/api/v1/admin/group-invites', {
    params: { query: params },
  });
  if (data === undefined) throwApiError(error);
  return data;
}

export async function fetchAdminGroupInvitesSummary(
  params: GroupInvitesSummaryParams,
) {
  const client = getApiClient();
  const { data, error } = await client.GET(
    '/api/v1/admin/group-invites/summary',
    { params: { query: params } },
  );
  if (data === undefined) throwApiError(error);
  return data;
}

export async function fetchAdminAudit(params: AuditPageParams) {
  const client = getApiClient();
  const { data, error } = await client.GET('/api/v1/admin/audit-log', {
    params: { query: params },
  });
  if (data === undefined) throwApiError(error);
  return data;
}

export async function fetchAdminConsent(params: ConsentPageParams) {
  const client = getApiClient();
  // `granted` travels as 'true'/'false' on the wire — the server's query
  // schema is an enum+transform because z.coerce.boolean() treated the
  // string "false" as true.
  const { granted, ...rest } = params;
  const { data, error } = await client.GET(
    '/api/v1/admin/consent-events',
    {
      params: {
        query: {
          ...rest,
          ...(granted === undefined ? {} : { granted: granted ? ('true' as const) : ('false' as const) }),
        },
      },
    },
  );
  if (data === undefined) throwApiError(error);
  return data;
}

export async function fetchAdminDsr(params: DsrPageParams) {
  const client = getApiClient();
  const { data, error } = await client.GET(
    '/api/v1/admin/dsr/requests',
    {
      params: {
        query: {
          cursor: params.cursor,
          pageSize: params.pageSize,
          status: params.status,
          kind: params.kind as
            | 'access'
            | 'rectification'
            | 'erasure'
            | 'restriction'
            | 'portability'
            | 'objection'
            | undefined,
        },
      },
    },
  );
  if (data === undefined) throwApiError(error);
  return data;
}

export async function fetchAdminDsrDetail(id: string) {
  const client = getApiClient();
  const { data, error } = await client.GET(
    '/api/v1/admin/dsr/requests/{id}',
    { params: { path: { id } } },
  );
  if (data === undefined) throwApiError(error);
  return data;
}

export async function fetchAdminSensitiveRead(
  params: SensitiveReadPageParams,
) {
  const client = getApiClient();
  const { data, error } = await client.GET(
    '/api/v1/admin/sensitive-read',
    { params: { query: params } },
  );
  if (data === undefined) throwApiError(error);
  return data;
}

export async function fetchAdminBroadcasts(params: BroadcastsPageParams) {
  const client = getApiClient();
  const { data, error } = await client.GET('/api/v1/admin/broadcasts', {
    params: {
      query: {
        cursor: params.cursor,
        pageSize: params.pageSize,
        status: params.status,
      },
    },
  });
  if (data === undefined) throwApiError(error);
  return data;
}

export async function fetchAdminBroadcastStatusCounts() {
  const client = getApiClient();
  const { data, error } = await client.GET(
    '/api/v1/admin/broadcasts/status-counts',
    {},
  );
  if (data === undefined) throwApiError(error);
  return data;
}

export async function fetchAdminBroadcastAudience(spec: AudienceSpec) {
  const client = getApiClient();
  const { data, error } = await client.GET(
    '/api/v1/admin/broadcasts/audience-resolve',
    { params: { query: { spec: JSON.stringify(spec) } } },
  );
  if (data === undefined) throwApiError(error);
  return data;
}

export async function fetchAdminModerationQueue(
  params: AdminReportQueueInput,
) {
  const client = getApiClient();
  const { data, error } = await client.GET(
    '/api/v1/admin/moderation/reports',
    { params: { query: params } },
  );
  if (data === undefined) throwApiError(error);
  return data;
}

export async function fetchAdminModerationReport(reportId: string) {
  const client = getApiClient();
  const { data, error, response } = await client.GET(
    '/api/v1/admin/moderation/reports/{id}',
    { params: { path: { id: reportId } } },
  );
  if (data === undefined) throwApiError(error, response);
  return data;
}

export async function fetchAdminModerationReportMessageContext(
  reportId: string,
) {
  const client = getApiClient();
  const { data, error, response } = await client.GET(
    '/api/v1/admin/moderation/reports/{id}/message-context',
    { params: { path: { id: reportId } } },
  );
  if (data === undefined) throwApiError(error, response);
  return data;
}

export async function fetchAdminModerationReportPostContext(reportId: string) {
  const client = getApiClient();
  const { data, error, response } = await client.GET(
    '/api/v1/admin/moderation/reports/{id}/post-context',
    { params: { path: { id: reportId } } },
  );
  if (data === undefined) throwApiError(error, response);
  return data;
}

export async function fetchAdminModerationStats() {
  const client = getApiClient();
  const { data, error } = await client.GET(
    '/api/v1/admin/moderation/stats',
    {},
  );
  if (data === undefined) throwApiError(error);
  return data;
}

export async function fetchAdminModerationStatusCounts() {
  const client = getApiClient();
  const { data, error } = await client.GET(
    '/api/v1/admin/moderation/status-counts',
    {},
  );
  if (data === undefined) throwApiError(error);
  return data;
}

export async function fetchAdminResourcesPage(
  params: ListAdminResourcesInput,
) {
  const client = getApiClient();
  const { data, error } = await client.GET('/api/v1/admin/resources', {
    params: { query: params },
  });
  if (data === undefined) throwApiError(error);
  return data;
}

export async function fetchAdminResourceDetail(resourceId: string) {
  const client = getApiClient();
  const { data, error, response } = await client.GET(
    '/api/v1/admin/resources/{id}',
    { params: { path: { id: resourceId } } },
  );
  if (data === undefined) throwApiError(error, response);
  return data.data;
}

export async function fetchAdminCirclesForPicker(params: PickerInput = {}) {
  const client = getApiClient();
  const { data, error } = await client.GET('/api/v1/admin/circles', {
    params: { query: params },
  });
  if (data === undefined) throwApiError(error);
  return data;
}

export async function fetchAdminNetworksForPicker(params: PickerInput = {}) {
  const client = getApiClient();
  const { data, error } = await client.GET('/api/v1/admin/networks', {
    params: { query: params },
  });
  if (data === undefined) throwApiError(error);
  return data;
}

export async function searchAdminMembersForPicker(params: PickerInput = {}) {
  const client = getApiClient();
  const { data, error } = await client.GET(
    '/api/v1/admin/members/search',
    { params: { query: params } },
  );
  if (data === undefined) throwApiError(error);
  return data;
}

export async function fetchAdminInterviewCandidates(
  params: InterviewCandidatesInput,
) {
  const client = getApiClient();
  const { data, error } = await client.GET(
    '/api/v1/admin/user-research/candidates',
    {
      params: {
        query: {
          segment: params.segment,
          activeDays: params.activeDays,
          limit: params.limit,
          // Query-string boolean: the server parses 'true'/'false' literals
          // (a bare boolean would arrive as the string "false" and coerce
          // truthy). Omit entirely when false — the server default.
          ...(params.includeContacted ? { includeContacted: 'true' as const } : {}),
        },
      },
    },
  );
  if (data === undefined) throwApiError(error);
  return data;
}

// ---- Hooks ----

export function useAdminMembersPage(params: ListAdminMembersInput) {
  // Flat reader reusing the infinite members-list leaf's key; supplies its own
  // non-paginated queryFn + keepPreviousData.
  return useQuery({
    queryKey: adminKeys.membersList(params).queryKey,
    queryFn: () => fetchAdminMembersPage(params),
    placeholderData: keepPreviousData,
  });
}

export function useInfiniteAdminMembers(
  input: Omit<ListAdminMembersInput, 'cursor'>,
) {
  return useInfiniteQuery(adminKeys.membersList(input));
}

export function useAdminMemberDetail(userId: string) {
  return useQuery(adminKeys.memberDetail(userId));
}

export function useAdminInterviewCandidates(params: InterviewCandidatesInput) {
  // keepPreviousData so switching segment/window filters swaps the list in
  // place instead of flashing the page empty.
  return useQuery({
    ...adminKeys.interviewCandidates(params),
    placeholderData: keepPreviousData,
  });
}

export function useAdminMemberStatusCounts() {
  return useQuery(adminKeys.memberStatusCounts());
}

export function useAdminInvitations(params: InvitationsPageParams) {
  return useQuery(adminKeys.invitations(params));
}

export function useReferralLinks() {
  return useQuery(adminKeys.referralLinks());
}

export function useAdminGroupInvites(params: GroupInvitesPageParams) {
  return useQuery(adminKeys.groupInvites(params));
}

export function useAdminGroupInvitesSummary(params: GroupInvitesSummaryParams) {
  return useQuery(adminKeys.groupInvitesSummary(params));
}

export function useAdminAudit(params: AuditPageParams) {
  return useQuery(adminKeys.audit(params));
}

export function useAdminConsent(params: ConsentPageParams) {
  return useQuery(adminKeys.consent(params));
}

export function useAdminDsr(params: DsrPageParams) {
  return useQuery(adminKeys.dsr(params));
}

export function useAdminDsrDetail(id: string) {
  return useQuery(adminKeys.dsrDetail(id));
}

export function useAdminSensitiveRead(params: SensitiveReadPageParams) {
  return useQuery(adminKeys.sensitiveRead(params));
}

export function useAdminBroadcasts(params: BroadcastsPageParams) {
  return useQuery(adminKeys.broadcastsList(params));
}

export function useAdminBroadcastStatusCounts() {
  return useQuery(adminKeys.broadcastStatusCounts());
}

export function useAdminBroadcastAudience(spec: AudienceSpec, enabled = true) {
  return useQuery({ ...adminKeys.broadcastAudience(spec), enabled });
}

export function useAdminModerationQueue(params: AdminReportQueueInput) {
  return useQuery(adminKeys.moderationQueue(params));
}

export function useAdminModerationReport(reportId: string) {
  return useQuery(adminKeys.moderationReport(reportId));
}

export function useAdminModerationReportMessageContext(
  reportId: string,
  opts: { enabled?: boolean } = {},
) {
  return useQuery({
    ...adminKeys.moderationReportMessageContext(reportId),
    enabled: reportId.length > 0 && (opts.enabled ?? true),
  });
}

export function useAdminModerationReportPostContext(
  reportId: string,
  opts: { enabled?: boolean } = {},
) {
  return useQuery({
    ...adminKeys.moderationReportPostContext(reportId),
    enabled: reportId.length > 0 && (opts.enabled ?? true),
  });
}

export function useAdminModerationStats() {
  return useQuery(adminKeys.moderationStats());
}

export function useAdminModerationStatusCounts() {
  return useQuery(adminKeys.moderationStatusCounts());
}

export function useAdminResourcesPage(params: ListAdminResourcesInput) {
  return useQuery(adminKeys.resourcesList(params));
}

export function useAdminResourceDetail(resourceId: string) {
  return useQuery(adminKeys.resourceDetail(resourceId));
}

export function useAdminCirclesForPicker(params: PickerInput = {}) {
  return useQuery(adminKeys.circlesPicker(params));
}

export function useAdminNetworksForPicker(params: PickerInput = {}) {
  return useQuery(adminKeys.networksPicker(params));
}

export function useInfiniteCirclesPicker(
  params: Omit<PickerInput, 'cursor'> = {},
) {
  return useInfiniteQuery(adminKeys.circlesPickerInfinite(params));
}

export function useInfiniteNetworksPicker(
  params: Omit<PickerInput, 'cursor'> = {},
) {
  return useInfiniteQuery(adminKeys.networksPickerInfinite(params));
}
